/**
 * Grouping and filtering for the Linear-style members list (#603
 * follow-on 3).
 *
 * A member is `is_active: false` from creation until they finish setup —
 * accepting the WorkOS invitation — and again if deactivated.
 * `deactivated_at` tells the two apart (#171), and they get separate
 * groups because a manager acts on them differently: resend the
 * invitation, or reactivate.
 */
import { MemberReturn } from '../clients/rust-api-client';
import { ListGroup } from '../shell/GroupedList';

export const MEMBER_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Not signed up' },
  { value: 'deactivated', label: 'Deactivated' },
] as const;

export type MemberStatus = (typeof MEMBER_STATUSES)[number]['value'];

export const memberStatus = (m: MemberReturn): MemberStatus =>
  m.deactivated_at ? 'deactivated' : m.is_active ? 'active' : 'invited';

export const memberStatusLabel = (m: MemberReturn) =>
  MEMBER_STATUSES.find((s) => s.value === memberStatus(m))!.label;

/**
 * The name a list shows. Full rather than preferred: the list is sorted by
 * surname, and first-name-only rows ("Bobby", "Jane", "John") read as
 * unsorted and lose what tells two Smiths apart. The preferred name stays
 * searchable, and the detail page shows it as "Goes by".
 */
export const memberListName = (m: MemberReturn) => m.full_name?.trim() || m.display_name;

export const memberInitials = (m: MemberReturn) =>
  [m.first_name, m.last_name]
    .map((n) => n?.trim()?.[0] ?? '')
    .join('')
    .toUpperCase() || '?';

export interface MemberFilters {
  statuses: string[];
  primaryOnly: string[]; // ['primary'] or []
  search: string;
}

export const applyMemberFilters = (members: MemberReturn[], f: MemberFilters) => {
  const q = f.search.trim().toLowerCase();
  return members.filter((m) => {
    if (f.statuses.length && !f.statuses.includes(memberStatus(m))) return false;
    if (f.primaryOnly.length && !m.is_primary) return false;
    if (q) {
      const hay = [m.display_name, m.full_name, m.email, m.phone].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

export const groupMembers = (members: MemberReturn[]): ListGroup<MemberReturn>[] => {
  const byName = [...members].sort((a, b) =>
    (a.last_name + a.first_name).localeCompare(b.last_name + b.first_name)
  );
  return MEMBER_STATUSES.map((s) => ({
    key: s.value,
    label: s.label,
    items: byName.filter((m) => memberStatus(m) === s.value),
  })).filter((g) => g.items.length > 0);
};
